import type { ApiClient } from "~/api/client";
import { SculptError } from "~/api/errors";
import { sectionInsertionMd, sectionMarkdown, spliceAt } from "~/shell/askbar/sectionInsertion";

/**
 * `edit-section` — the only intent that touches a document body.
 *
 * Three facts kms's version is easy to misread, and this one does not:
 *
 * 1. The `/ai-action` call is **not** the permission check. `AuthorizeAiAsync(id, "propose")`
 *    needs only resource `Read`, so a read-only viewer passes it. The real gate already fired:
 *    the *route* call 403s `edit_refused` for anyone without `update`. The call is kept for its
 *    403 branch and for nothing else (§E6.3).
 * 2. On a **v1** document it is therefore always a 409 `not_on_v2_lane`, because the lane check
 *    runs after the authorization. A 409 here is success.
 * 3. The write goes through `documents.sculpt` (the caller's `saveBody`), never an editor
 *    handle — checkpointed, revertible, collab-safe, and possible with no scene mounted (§C5).
 */

export class EditSectionRefused extends Error {
  readonly reason: "permission" | "authorize";
  constructor(reason: "permission" | "authorize", message: string) {
    super(message);
    this.name = "EditSectionRefused";
    this.reason = reason;
  }
}

export interface EditSectionInput {
  api: ApiClient;
  documentId: string;
  heading: string | null;
  body: string;
  saveBody: (markdown: string) => Promise<unknown>;
}

export async function runEditSection({
  api,
  documentId,
  heading,
  body,
  saveBody,
}: EditSectionInput): Promise<{ heading: string | null; underExisting: boolean }> {
  try {
    const decision = await api.editorV2.aiAction(documentId, {
      capability: "edit-section",
      from: 0,
      to: 0,
      requestedLane: "propose",
    });
    if (decision === null) {
      throw new EditSectionRefused("permission", "You don't have permission to edit this document");
    }
  } catch (error) {
    if (error instanceof EditSectionRefused) {
      throw error;
    }
    // The expected outcome on the lane 99% of documents are on. Not a failure.
    if (!(error instanceof SculptError && error.code === "not_on_v2_lane")) {
      throw new EditSectionRefused("authorize", "Couldn't authorize the edit");
    }
  }

  // Re-read immediately before the splice — one tick of lost-update window, and sculpt's
  // checkpoint is what makes the remainder recoverable (§E2 R5).
  const document = await api.documents.get(documentId);
  const { pos, underExisting } = sectionInsertionMd(document.text, heading);
  const next = spliceAt(document.text, pos, sectionMarkdown(heading, body, underExisting));
  await saveBody(next);
  return { heading, underExisting };
}
